import {
  useISirklogMessagePublish,
  useISirkView,
} from "features/CenterContent/RoleContent/ISirk/useISirkData";
import { FC, FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line drops/no-direct-spor-import
import {
  Box,
  Field,
  FieldErrorText,
  Stack,
  Text,
  Textarea,
} from "@vygruppen/spor-react";

import { SpeechBubbleOutline24Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import type { CreateIncidentLogMessageSchema } from "features/CenterContent/RoleContent/ISirk/types";
import { useISirkLogMessages } from "features/CenterContent/RoleContent/ISirk/useISirkLogMessages";
import { LogEntry } from "features/CenterContent/VehicleDetails/shared/LogEntry";
import { ActionCard } from "shared/components/drops/ActionCard";
import { ActionCardButton } from "shared/components/drops/ActionCardButton";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { compareDateStringAsc } from "shared/utils/dateTimeUtils";

type Props = {
  selectedDate: string;
  shiftId: string;
  operatorName?: string;
};

export const ISirkModuleForShifts: FC<Props> = ({
  selectedDate,
  shiftId,
  operatorName,
}) => {
  const { t } = useTranslation();
  const formId = useId();
  const { data } = useISirkView(selectedDate);

  const [logMessage, setLogMessage] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Extract ISirk from shift number
  const isirkFromShift = shiftId.slice(0, 3);

  const filterIsirkBasedOnShift = data?.incidentNotes
    ?.filter((note) => note.isirk.endsWith(isirkFromShift))
    .map((note) => note.isirk);

  const iSirk = filterIsirkBasedOnShift?.toString();

  const { data: isirkMessage } = useISirkLogMessages(iSirk);

  const { postIncidentLog, mutationStatus } = useISirklogMessagePublish();

  const predefinedText = `For ${shiftId} / ${operatorName} / ${format(
    new Date(selectedDate),
    "dd.MM.yy",
  )}: `;

  const filteredLogs =
    isirkMessage?.incidentLogs?.filter((log) => {
      const msg = log.message ?? "";
      return msg.includes(shiftId) && msg.includes(operatorName ?? "");
    }) ?? [];

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = logMessage.trim();
    if (!trimmedMessage) {
      setHasSubmitted(true);
      return;
    }
    if (!iSirk || mutationStatus === "pending") return;

    const payload: CreateIncidentLogMessageSchema = {
      message: `${predefinedText.trimEnd()} ${trimmedMessage}`,
      isirkId: iSirk,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload, {
      onSuccess: () => {
        setLogMessage((currentMessage) =>
          currentMessage === logMessage ? "" : currentMessage,
        );
        setHasSubmitted(false);
      },
    });
  };

  const isLogMessageInvalid = hasSubmitted && !logMessage.trim();

  return (
    <>
      <ActionCard
        title={t("Ny I-sirk-melding")}
        icon={<SpeechBubbleOutline24Icon aria-hidden="true" />}
        primaryAction={
          <ActionCardButton
            form={formId}
            type="submit"
            variant="secondary"
            loading={mutationStatus === "pending"}
            disabled={!iSirk || mutationStatus === "pending"}
          >
            {t("Send melding")}
          </ActionCardButton>
        }
      >
        <form id={formId} onSubmit={onSubmit}>
          <Stack align="stretch" gap="2">
            <Field invalid={isLogMessageInvalid} w="100%">
              <Textarea
                label={t("Skriv loggmelding")}
                name="logmessage"
                value={logMessage}
                onChange={(event) => setLogMessage(event.target.value)}
                aria-invalid={isLogMessageInvalid}
                minHeight="7rem"
              />
              <FieldErrorText>
                {isLogMessageInvalid ? t("Dette feltet må fylles ut") : ""}
              </FieldErrorText>
            </Field>
          </Stack>
        </form>
      </ActionCard>
      {mutationStatus === "error" && (
        <FailureMessage
          customMessage={t(
            "Kunne ikke sende loggmelding. Prøv igjen, eller kontakt IT hvis feilen vedvarer.",
          )}
        />
      )}

      <Stack gap="0" align="stretch" mt={filteredLogs.length > 0 ? 2 : 0}>
        {filteredLogs
          .sort((a, b) => compareDateStringAsc(a.timestamp, b.timestamp))
          .map((log, index) => (
            <LogEntry
              key={log.id}
              timestamp={log.timestamp}
              message={
                log.message?.startsWith(predefinedText)
                  ? log.message.slice(predefinedText.length).trimStart()
                  : log.message
              }
              source={log.sourceSystem.author?.split("@")[0]}
              fallbackSource={log.sourceSystem.name}
              hideDashedLine={index === filteredLogs.length - 1}
            />
          ))}
        {filteredLogs.length === 0 && (
          <Box padding="2" paddingTop="4">
            <Text fontWeight={400} color="text.subtle">
              Ingen meldinger i loggen
            </Text>
          </Box>
        )}
      </Stack>
    </>
  );
};
